import { getPageType } from '~/events/utils/pageType'
import productTransformer from './transformers/product.transformer'

export default ({ eventBus, eventDefinitions, context }) => {
  eventBus.$on(eventDefinitions.ORDER.PLACED, handlePurchase)

  function handlePurchase({ order }) {
    if (!order || !order.items) {
      return
    }

    const transformedItems = order.items
      .filter(isTrackablePurchaseItem)
      .map((item, i) =>
        productTransformer(
          item.product,
          item.amount,
          getOrderItemUnitWithoutTax(item),
          getOrderItemUnitWithTax(item),
          i,
          item,
          item.attributes?.custom?.colour ?? null,
          item.attributes?.custom?.master_item_id ?? null,
        ),
      )
    if (transformedItems.length === 0) {
      return
    }

    const { totalValue, totalValueVat, mktValAll, tax } = calculateOrderTotals(order)
    const shippingFields = getShippingFields(order)
    const paymentFields = getPaymentFields(order)
    const customerInfoFields = getUserPurchasePropertiesFields(order)

    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: order.code,
        value: totalValue,
        value_vat: totalValueVat,
        tax: tax,
        ...shippingFields,
        currency: order.currencyCode,
        coupon: '',
        ...paymentFields,
        mkt_val_all: mktValAll,
        items: transformedItems,
        google_tag_params: {
          ecomm_pagetype: 'purchase',
          ecomm_prodid: transformedItems.map(item => String(item.item_id)),
          ecomm_totalvalue: totalValue,
          ecomm_totalvalue_all: transformedItems.map(item => item.price * item.quantity),
        },
        ...customerInfoFields,
      },
    })
  }

  function isTrackablePurchaseItem(item) {
    if (!item || item.attributes?.discount || !item.product) {
      return false
    }

    const hasDirectPrice = getOrderItemUnitWithoutTax(item) > 0 || getOrderItemUnitWithTax(item) > 0
    const hasChildren = Array.isArray(item.items) && item.items.length > 0

    return hasDirectPrice || hasChildren
  }

  function getOrderItemUnitWithoutTax(item) {
    const directPrice = Number(item?.prices?.unitWithoutTax || 0)

    if (directPrice > 0) {
      return directPrice
    }

    const totalPrice = Number(item?.prices?.totalWithoutTax || 0)

    if (totalPrice > 0) {
      const amount = Number(item?.amount || 1)

      return totalPrice / amount
    }

    return (item?.items || []).reduce((sum, child) => sum + getOrderItemUnitWithoutTax(child), 0)
  }

  function getOrderItemUnitWithTax(item) {
    const directPrice = Number(item?.prices?.unitWithTax || 0)

    if (directPrice > 0) {
      return directPrice
    }

    const totalPrice = Number(item?.prices?.totalWithTax || 0)

    if (totalPrice > 0) {
      const amount = Number(item?.amount || 1)

      return totalPrice / amount
    }

    return (item?.items || []).reduce((sum, child) => sum + getOrderItemUnitWithTax(child), 0)
  }

  /**
   * Calculates cart total values.
   */
  function calculateOrderTotals(order) {
    return {
      totalValue: order.prices.itemsWithoutTax,
      totalValueVat: order.prices.itemsWithTax,
      mktValAll: order.prices.itemsWithoutTax,
      tax: order.prices.itemsWithTax - order.prices.itemsWithoutTax,
    }
  }

  /**
   * Extracts shipping fields based on the checkout option.
   */
  function getShippingFields(order) {
    return {
      shipping_tier: order.shipper.name || 'Unknown',
      shipping: order.prices.shipperPriceWithoutTax || 0.0,
      shipping_vat: order.prices.shipperPriceWithTax || 0.0,
    }
  }

  /**
   * Extracts payment fields if the option is 'add_customer_info'.
   */
  function getPaymentFields(order) {
    return {
      payment_type: order.payment.name || 'Unknown',
      payment_fee: order.prices.paymentPriceWithTax || 0.0,
    }
  }

  /**
   * Extracts user purchase properties fields based on the checkout option.
   */
  function getUserPurchasePropertiesFields(order) {
    return {
      purchase_user_properties: {
        user_id: order?.attributes?.userId || '',
        customer_type: order.customer?.tin ? 'B2B' : 'B2C',
        email: order.customer?.email || '',
        phone: order.customer?.phone ? order.customer.phone.replace(/\s+/g, '') : '',
        address: {
          name: order.customer?.firstName || '',
          surname: order.customer?.lastName || '',
          street: order.billingAddress?.street || '',
          no: order.billingAddress?.streetNumber || '',
          city: order.billingAddress?.city || '',
          region: '',
          country: order.billingAddress?.countryAlpha2 || '',
          zip: order.billingAddress?.postcode || '',
        },
      },
    }
  }
}
